import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const RegistrarRol = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const rolFromState = state?.rol;

  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);

  const [nombreRol, setNombreRol] = useState("");
  const [descripcionRol, setDescripcionRol] = useState("");
  const [estadoRol, setEstadoRol] = useState(true);

  useEffect(() => {
    setEdit(!!id);
  }, [id]);

  const cleanData = () => {
    setNombreRol("");
    setDescripcionRol("");
    setEstadoRol(true);
  };

  const handleCancelOrClean = () => {
    if (edit) {
      navigate(-1);
    } else {
      cleanData();
    }
  };

  useEffect(() => {
    const fetchRol = async () => {
      try {
        if (rolFromState) {
          setNombreRol(rolFromState.nombreRol || "");
          setDescripcionRol(rolFromState.descripcionRol || "");
          setEstadoRol(
            typeof rolFromState.estadoRol === "boolean" ? rolFromState.estadoRol : true
          );
          return;
        }

        const { data } = await axios.get(`/api/seguridad/roles/listid/${id}`);

        if (data?.ok) {
          const rol = data.rol || {};
          setNombreRol(rol.nombreRol || "");
          setDescripcionRol(rol.descripcionRol || "");
          setEstadoRol(typeof rol.estadoRol === "boolean" ? rol.estadoRol : true);
        }
      } catch (error) {
        console.error("Error al obtener rol:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "No se pudo cargar el rol",
        });
      }
    };

    if (id) fetchRol();
  }, [id, rolFromState]);

  const saveRol = async () => {
    try {
      if (!nombreRol.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "Debes ingresar el nombre del rol",
        });
      }

      setLoading(true);

      const payload = {
        nombreRol: nombreRol.trim(),
        descripcionRol: descripcionRol.trim(),
        estadoRol,
      };

      const { data } = await axios.post("/api/seguridad/roles/create", payload);

      if (data?.ok) {
        Swal.fire({
          icon: "success",
          title: data?.message || "Rol registrado correctamente",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          cleanData();
          navigate(-1);
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Atención",
          text: data?.message || "No fue posible registrar el rol",
        });
      }
    } catch (error) {
      console.error("Error al registrar rol:", error);
      Swal.fire({
        icon: "error",
        title: "Error al registrar",
        text: error.response?.data?.message || "Intenta nuevamente",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRol = async () => {
    try {
      if (!nombreRol.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Campo requerido",
          text: "Debes ingresar el nombre del rol",
        });
      }

      setLoading(true);

      const payload = {
        nombreRol: nombreRol.trim(),
        descripcionRol: descripcionRol.trim(),
        estadoRol,
      };

      const { data } = await axios.put(`/api/seguridad/roles/edit/${id}`, payload);

      Swal.fire({
        icon: "success",
        title: data?.message || "Rol actualizado correctamente",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => {
        navigate(-1);
      });
    } catch (error) {
      console.error("Error al actualizar rol:", error);
      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text: error.response?.data?.message || "Intenta nuevamente",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (edit) {
      await updateRol();
    } else {
      await saveRol();
    }
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 fw-semibold">
                {edit ? "Actualizar Rol" : "Registrar Rol"}
              </h4>
              <p className="mb-0 text-secondary small">
                Diligencia la información del rol. Los campos marcados son obligatorios.
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

              <button
                type="button"
                className={`btn ${edit ? "btn-outline-danger" : "btn-outline-secondary"}`}
                onClick={handleCancelOrClean}
                disabled={loading}
              >
                <i className={`fa-solid ${edit ? "fa-xmark" : "fa-eraser"} me-2`}></i>
                {edit ? "Cancelar" : "Limpiar"}
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={actions}>
          <div className="row justify-content-center">
            <div className="col-12 col-xl-8">
              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0 fw-semibold">Información del rol</h6>
                  <span className="text-secondary small">* Obligatorio</span>
                </div>

                <hr className="my-3" />

                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Nombre del rol *
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={nombreRol}
                      onChange={(e) => setNombreRol(e.target.value)}
                      placeholder="Ej: Administrador"
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Descripción
                    </label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={descripcionRol}
                      onChange={(e) => setDescripcionRol(e.target.value)}
                      placeholder="Describe la función o alcance del rol"
                    ></textarea>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Estado
                    </label>
                    <select
                      className="form-select"
                      value={estadoRol ? "true" : "false"}
                      onChange={(e) => setEstadoRol(e.target.value === "true")}
                    >
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="row g-3 mt-3">
                <div className="col-12 col-md-6">
                  <button
                    type="submit"
                    className="btn btn-dark w-100 fw-semibold py-2"
                    disabled={loading}
                  >
                    {loading ? "Guardando..." : edit ? "Guardar cambios" : "Registrar rol"}
                  </button>
                </div>

                <div className="col-12 col-md-6">
                  <button
                    type="button"
                    className={`btn w-100 fw-semibold py-2 ${
                      edit ? "btn-outline-danger" : "btn-outline-secondary"
                    }`}
                    onClick={handleCancelOrClean}
                    disabled={loading}
                  >
                    {edit ? "Cancelar" : "Limpiar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="mt-4 text-center text-secondary small">
          Verifica la información antes de {edit ? "guardar cambios" : "registrar"} el rol.
        </div>
      </div>
    </div>
  );
};

export default RegistrarRol;